import { useState, useRef, useEffect } from 'react'
import { useNavigate, useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '../../hooks/useAuth'
import useTitle from '../../hooks/useTitle'
import SpreadSelector from '../../tarot-components/SpreadSelector'
import FlowStep from '../../tarot-components/flow/FlowStep'
import { SPREAD_TYPES } from '../../tarot-data/cards'
import { SpreadFlow } from '../../tarot-components/spreads'
import DrawnCardBanner from '../../components/DrawnCardBanner'
import '../../tarot-components/SpreadSelector.css'
import '../../tarot-components/CardSpread.css'
import '../../tarot-components/CardHoverInfo.css'
import '../../tarot-components/TarotCard.css'
import '../../tarot-components/ReadingDisplay.css'

/**
 * The reading page: one column the reading grows down. Choosing a spread
 * locks the choice and deals the spread's steps in underneath it.
 */
const Welcome = () => {
  const { username } = useAuth()
  const { t } = useTranslation()
  useTitle(t('titles.reading', { username }))

  // The URL is the source of truth for which spread is open, so a direct
  // link, a refresh or the browser's back button all land on the right view.
  const { spreadId } = useParams()
  const selectedSpread = Object.values(SPREAD_TYPES).find(s => s.id === spreadId) ?? null
  const navigate = useNavigate()

  // Bumped each time a reading is closed, so the spread list comes back
  // fresh — dealt in again — rather than just unlocking.
  const [round, setRound] = useState(0)
  const hadSpread = useRef(Boolean(spreadId))

  // Leaving a spread, by "New reading" or the browser's back button, clears
  // it away and takes the reader back to the top to choose again.
  useEffect(() => {
    if (spreadId) {
      hadSpread.current = true
      return
    }
    if (!hadSpread.current) return
    hadSpread.current = false
    setRound(r => r + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [spreadId])

  if (spreadId && !selectedSpread) {
    return <Navigate to="/dash" replace />
  }

  return (
    <div className="app reading-page">
      {!selectedSpread && <DrawnCardBanner />}
      <FlowStep key={round} name="choose" locked={Boolean(selectedSpread)}>
        <SpreadSelector
          selectedId={spreadId}
          locked={Boolean(selectedSpread)}
          onSelectSpread={(spread) => navigate(`/dash/spread/${spread.id}`)}
        />
      </FlowStep>

      {selectedSpread && (
        // Keyed by spread, so each reading starts from a fresh deck
        <SpreadFlow
          key={selectedSpread.id}
          spread={selectedSpread}
          onNewReading={() => navigate('/dash')}
        />
      )}
    </div>
  )
}

export default Welcome
